import {
  isCurrentShelemView,
  type ShelemHandResult,
  type ShelemMove,
  type ShelemView,
} from "@bing/engine-shelem";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard, type MatchEndForm } from "../components/MatchEndCard";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import {
  getBotDifficulty,
  getCardDeck,
  getShelemTarget,
  setBotDifficulty,
  setCardDeck,
  setShelemTarget,
} from "../settings/prefs";
import type { BotDifficulty, CardDeckId, ShelemTarget } from "@bing/shared";
import { SegmentedChoice } from "../components/SegmentedChoice";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { StartRow } from "../components/StartScreenShell";
import { takeResumeIntent } from "./fresh-solo";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { faDigits } from "../i18n/digits";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import { ShelemTableOrUpdating } from "./shelem-old-server";
import { SHELEM_END_REVEAL_MS } from "./shelem-beats";
import { LastHandBox, signedScore } from "./shelem-hand-result";
import { shelemSeatFallback } from "./shelem-seat-label";
import { SHELEM_TARGET_CHOICES } from "./shelem-targets";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";
import { CardDeckContext } from "./card-decks";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";

const AVATAR_IDS = ["lion", "wolf", "tiger", "eagle"] as const;

/**
 * WHY THE MATCH ENDED, true for whoever reads it (P1 Task 7 — the دوز way, `DoozGame.tsx`), or null
 * when the card has nothing to go on (a caller that hands it no frame):
 *  ① THE ROOM'S REASON WINS WHEN THE BOARD HAS NONE. A four-seat table's terminal carries ONE —
 *    `"opponents-left"`, the partnership rule (`endIfTeamAbandoned`: both chairs of a team gone, by a
 *    «خروج», an idle removal or a lapsed grace alike) — and only the team that STAYED reads it: the
 *    other team has no chair left to read it from. `"idle"` / `"resign"` end a match only at a table
 *    for two (`forfeitTwoSeatTable`), so no copy is invented for them here — and a room-decided end
 *    the card has no words for reads no reason at all (null): the board did not end that match, so
 *    none of ②'s lines is true of it (fix round 1: the loser handed «opponents-left» after 9 hands
 *    read «پایان ۹ دست — امتیاز بیشتر برد», false twice over).
 *  ② OTHERWISE THE BOARD — the ENGINE's own terminal, which carries no reason (`score()`): an exact
 *    tie goes to the team that held the last hand's contract (D14 — never a draw); no team at the
 *    target means the played-hand cap ended it (the engine's only other end), the higher total
 *    winning; else the winners reached the target.
 */
export function shelemEndReason(args: {
  result: GameResult;
  won: boolean;
  teamScores: readonly number[];
  targetScore: number | undefined;
  playedHands: number | undefined;
}): string | null {
  const { result, won, teamScores, targetScore, playedHands } = args;
  if (result.reason !== undefined)
    return result.reason === "opponents-left" && won ? fa.shelem.endTheyLeft : null;
  if (targetScore === undefined) return null;
  const [t0 = 0, t1 = 0] = teamScores;
  if (t0 === t1) return fa.shelem.endTie;
  if (!teamScores.some((score) => score >= targetScore))
    return playedHands === undefined ? null : fa.shelem.endCap(faDigits(playedHands));
  return won
    ? fa.shelem.endWeReached(faDigits(targetScore))
    : fa.shelem.endTheyReached(faDigits(targetScore));
}

/** The design's win overlay, scored by TEAM: the two partnerships ranked — the WINNING team first
 * (D14), then by match score — each row its two players' faces and REAL names, the viewer's team
 * highlighted; why the match ended (`shelemEndReason`), the last hand's line and the target (P2-4);
 * plus play-again / exit. `result.winners` holds every seat on the winning team.
 * Exported so the online Shelem game reuses the exact same end-of-match overlay. */
export function WinOverlay({
  result,
  teamScores,
  you,
  self,
  players,
  lastHand,
  targetScore,
  playedHands,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  result: GameResult;
  teamScores: readonly number[];
  you: number;
  /** The signed-in user's identity — their own face on the team row is really theirs. */
  self?: SeatIdentity | null;
  /**
   * The lobby's known seat occupants. ⚠️ شلم's card ranks TEAMS, not seats, so it never printed
   * «بازیکن N» and there is no NAME here to correct — but the four faces on the two team rows
   * were positional stand-ins for the same reason پاسور's and بلوف's were: the roster never
   * reached the overlay. Threading it in gives each team-mate their own photo and badge.
   */
  players?: readonly SeatPlayer[];
  /** The frame's last hand — its line under the team rows (P2-4). Absent: no line. */
  lastHand?: ShelemHandResult | null;
  /** The match's target — «بازی تا ۱۲۰۰» and the reason's «… به ۱۲۰۰ رسید». Absent: neither. */
  targetScore?: number;
  /** Hands played to the end (redeals excluded) — the cap's reason. */
  playedHands?: number;
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(you);
  // The preset is the OLD positional face, kept verbatim, so a seat with no known human still
  // looks exactly as it did (solo, and any frame before the roster lands).
  const avatarOf = (seat: number) =>
    resolveSeatAvatar(seat, you, self, players, (s) =>
      s === you ? "lion" : (AVATAR_IDS[s % AVATAR_IDS.length] ?? "fox"),
    );
  // the felt's own names (`resolveSeatName` + the felt's fallback): the card and the felt call each
  // chair the same — a real player by name, a bot by its positional label
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.shelem.you,
      fallback: (s) => shelemSeatFallback(s, you, teamScores.length * 2),
    });
  const reason = shelemEndReason({ result, won, teamScores, targetScore, playedHands });
  const myTeam = you % 2;
  // The WINNING team first, whatever ended the match — then the higher total. The 🥇 is the
  // winners' row, as the title is. Ranked by score, it went to the LOSERS twice over: an exact tie
  // (D14 gives it to the team that held the last hand's contract) put «تیم ۱» on top, and a
  // partnership FORFEIT — one whole team gone, the other declared the winner by the game-server
  // (`teamAbandonScore`), whatever the score — put the leavers on top whenever they were ahead,
  // beside «تیم شما برد» for the team that stayed. `winners` names the team in every case.
  const winningTeam = result.winners.length > 0 ? result.winners[0]! % 2 : -1;
  const ranked = teamScores
    .map((score, team) => ({ team, score }))
    .sort(
      (a, b) =>
        Number(b.team === winningTeam) - Number(a.team === winningTeam) || b.score - a.score,
    );
  const medals = ["🥇", "🥈"];
  // «بازی تا N» under the last hand — where the reason already names N (`… به N رسید`) the tight card
  // drops it
  const reasonNamesTarget =
    targetScore !== undefined &&
    (reason === fa.shelem.endWeReached(faDigits(targetScore)) ||
      reason === fa.shelem.endTheyReached(faDigits(targetScore)));
  // …per the card's measured form, as P1 Task 9a/9b measured it: the full card always, the tight one
  // unless the reason names N, the tighter one never
  const shownTarget = (form: MatchEndForm): number | null =>
    targetScore === undefined || form === "tighter" || (form === "tight" && reasonNamesTarget)
      ? null
      : targetScore;
  // card-98 item 12: the shared template — the winning feel, a row a TEAM (its two faces and names,
  // the medal, the signed total), and this card's own words under them: the last hand's box (item 11)
  // and the target
  return (
    <MatchEndCard
      label={fa.shelem.gameOver}
      outcome={won ? "won" : "lost"}
      glyph="🎴"
      title={won ? fa.shelem.youWon : fa.shelem.youLost}
      reason={reason ?? fa.shelem.scores}
      sides={ranked.map((r, i) => {
        const teamSeats = [r.team, r.team + 2].filter((s) => s < teamScores.length * 2);
        return {
          key: `team-${r.team}`,
          mine: r.team === myTeam,
          mark: medals[i],
          members: teamSeats.map((s) => ({ seat: s, name: nameOf(s), avatar: avatarOf(s) })),
          result: signedScore(r.score),
          attrs: { "data-team": r.team },
        };
      })}
      extra={(form) =>
        lastHand || shownTarget(form) !== null ? (
          <>
            {/* the last hand's box (item 11) — with less air in the tight forms */}
            {lastHand ? (
              <LastHandBox
                last={lastHand}
                you={you}
                hakemName={nameOf(lastHand.declarer)}
                compact={form !== "full"}
                bare={form === "tighter"}
              />
            ) : null}
            {/* «بازی تا N» — the first thing the TIGHTER card gives up (Task 9b: 320×568 ×1.5) */}
            {shownTarget(form) !== null ? (
              <div
                data-testid={lastHand ? undefined : "shelem-end-last-hand"}
                data-end-target=""
                style={{ fontSize: 13, color: "var(--hint)", marginTop: lastHand ? 6 : 0 }}
              >
                {fa.shelem.playedTo(faDigits(shownTarget(form)!))}
              </div>
            ) : null}
          </>
        ) : null
      }
      promo
      playAgain={fa.shelem.playAgain}
      leave={fa.shelem.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHintTighter={
        againHint === fa.online.rematchWaiting ? fa.shelem.rematchWaitingShort : undefined
      }
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Owns the Shelem connection + store, and renders connect/error/table/game-over. */
function ShelemSoloRound({
  difficulty,
  target,
  onAgain,
}: {
  /** FIXED for this game — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  /** The match target picked on the start screen (card-67) — FIXED likewise. A resume sends it too
   *  and the server ignores it there: the save keeps the target it was dealt under. */
  target: ShelemTarget;
  onAgain: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  // your own seat is YOU — your photo and your name, as on every other solo table (W23, P1-13)
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore<ShelemMove, ShelemView>(
        createColyseusGameConnector<ShelemMove, ShelemView>({
          wsUrl: GAME_SERVER_URL,
          room: "shelem",
          getInitData: () => initData,
          // FIXED for this game — chosen on the start screen, never changes mid-game.
          difficulty,
          shelemTarget: target,
        }),
      ),
    // `round` bumps to start a fresh game (new connection) on "play again".
    [initData, round, difficulty, target],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay until the match's LAST collect and its LAST hand-end card have both
  // been shown (P1 Task 7 — the operator's «always shown»): SHELEM_END_REVEAL_MS, not the shell's
  // one beat — online, the same number whenever the last frame carries a hand end
  // (`ShelemOnlineGame`'s `endRevealMs`, read off that frame: a room-decided end mid-hand has none
  // to wait for). Solo's only end is the engine's, whose frame always carries one.
  const showEnd = useEndReveal(result !== null, SHELEM_END_REVEAL_MS);
  // The win/lose chime lands WITH the summary (it used to fire ~1.4s early).
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  // The classic SVG faces are a separate chunk (see `game/card-faces.ts`): the table may not
  // be revealed until they are in, or it would deal blank cards. The bar's floor is a MINIMUM,
  // not a timeout — on a slow connection it simply waits longer.
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per hand from the felt's first card — no hand while the
  // loading screen is up (`hand-deck.ts`, N1) — a pick lands at the next deal, never mid-hand
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("shelem", view));

  // «خروج» — leave but KEEP the game resumable (a plain disconnect no longer ends it).
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack (the
  // ActiveGame row is provably deleted) BEFORE dropping it locally and leaving. On
  // failure/timeout stay in the game, keep it listed as in-progress (it IS), and
  // surface a retry dialog — never navigate-and-forget an unconfirmed end.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("shelem"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  // The in-game exit is gated behind the two-option confirm dialog first.
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» returns to the START SCREEN rather than re-dealing at the same settings —
  // the same flow حکم/شطرنج/منچ/تخته‌نرد use, so the next game can be set up before it deals.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.shelem.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {fa.shelem.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    // Retries are spent and the artwork still is not here: an explicit, recoverable screen —
    // never a silent stall, and never a table dealt with blank cards.
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : fa.shelem.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.shelem.leave}
      />
    );
  }

  // A frame the table cannot draw — the OLD game-server's (`isCurrentShelemView`; the group review's
  // I-3, as پاسور's): the maintenance notice and a way out that leaves AT ONCE, the save kept — no
  // «keep or end» dialog (an end sent to the old game-server could end the save it cannot draw), and
  // no end card drawn off a view it cannot read
  if (!isCurrentShelemView(view))
    return <Boot error={fa.errors.MAINTENANCE} onLeave={doLeave} leaveLabel={fa.shelem.leave} />;

  return (
    <CardDeckContext.Provider value={drawDeck}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. An
          OLD game-server's frame draws the «updating» notice instead (above, and `shelem-old-server.tsx`). */}
      <ShelemTableOrUpdating
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
      />
      {result && showEnd ? (
        // Already over (ActiveGame cleared by the terminal finish) → plain exit.
        <WinOverlay
          result={result}
          teamScores={view.teamScores}
          you={view.you}
          // the same identity the felt drew: your own face on your team's row
          self={profile}
          lastHand={view.lastHand}
          targetScore={view.targetScore}
          playedHands={view.playedHands}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        // The server never ACKed the end — the game is still in progress and still
        // on the resume list. Same shared dialog, as the error surface: retry or stay.
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/**
 * Owns the START SCREEN → game flow. The match target (card-67), the opponent level and the card
 * design are chosen here, before the deal, and stay FIXED for the game; «بازی دوباره» comes back to
 * ask again.
 *
 * THE TARGET is the private table's own control and list (`SegmentedChoice`, `SHELEM_TARGET_CHOICES`):
 * ۶۶۰ on first use, then REMEMBERED PER DEVICE (`bing:shelemTarget`), the way حکم's solo pickers are
 * — the coordinator's ruling on the review, card-67. The server validates the pick as a private
 * host's and ignores it on a resume.
 *
 * A RESUME stands the screen aside ({@link takeResumeIntent}): the saved game already carries
 * the settings it began with, and the lobby arms that signal for every solo resume.
 */
export function ShelemGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [target, setTarget] = useState<ShelemTarget>(getShelemTarget());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());

  if (phase === "select") {
    return (
      <SoloStartScreen
        title={fa.shelem.title}
        emoji="💰"
        heading={fa.soloStart.heading}
        leaveLabel={fa.shelem.leave}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      >
        {/* the rules of the match first (`SoloStartScreen`): how many points win it */}
        <StartRow label={fa.shelem.chooseTarget}>
          <SegmentedChoice
            label={fa.shelem.chooseTarget}
            options={SHELEM_TARGET_CHOICES}
            value={target}
            onChange={(v) => {
              setTarget(v);
              setShelemTarget(v);
            }}
            compact
          />
        </StartRow>
      </SoloStartScreen>
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered: App computes
  // `getCardDeck()` once, above this component, and never re-renders on a phase flip — so the
  // pick would not reach the table without this nested provider (the inner one wins).
  return (
    <CardDeckContext.Provider value={deck}>
      <ShelemSoloRound difficulty={difficulty} target={target} onAgain={() => setPhase("select")} />
    </CardDeckContext.Provider>
  );
}
